import * as THREE from 'three';
import { merge, part, sharedMaterials } from './geometry';

/**
 * Weapon models. Authored with the grip at the origin and the blade/shaft extending along -Y, so that
 * when parented to a hand socket the weapon continues the line of the forearm.
 */
export function buildWeapon(model: string | null | undefined, tint?: string): THREE.Object3D | null {
  if (!model) return null;
  const g = new THREE.Group();
  g.name = `weapon:${model}`;
  switch (model) {
    case 'sword_wood':
    case 'sword_iron':
    case 'sword_steel':
    case 'sword_jade':
      g.add(buildJian(model, tint));
      break;
    case 'saber':
      g.add(buildSaber());
      break;
    case 'staff':
      g.add(
        new THREE.Mesh(
          merge([
            part.cyl(0.022, 0.026, 1.7, '#6b4a2b', { pos: [0, -0.55, 0] }),
            part.sphere(0.045, '#8a6a3a', { pos: [0, 0.32, 0] }),
          ]),
          sharedMaterials.cloth,
        ),
      );
      break;
    case 'spear':
      g.add(
        new THREE.Mesh(
          merge([
            part.cyl(0.02, 0.022, 2.1, '#5a3a22', { pos: [0, -0.35, 0] }),
            part.cone(0.04, 0.26, '#cfd5da', { pos: [0, 0.82, 0] }, 4),
            part.cone(0.05, 0.12, '#a52a24', { pos: [0, 0.64, 0], rot: [Math.PI, 0, 0] }, 8),
          ]),
          sharedMaterials.metal,
        ),
      );
      break;
    case 'hammer':
      g.add(
        new THREE.Mesh(
          merge([
            part.cyl(0.018, 0.02, 0.45, '#5a3a22', { pos: [0, -0.18, 0] }),
            part.box(0.09, 0.09, 0.18, '#4a4d52', { pos: [0, -0.42, 0] }),
          ]),
          sharedMaterials.metal,
        ),
      );
      break;
    case 'basket':
      g.add(
        new THREE.Mesh(
          merge([
            part.cyl(0.16, 0.12, 0.14, '#b08a52', { pos: [0, -0.16, 0.05] }),
            part.torus(0.1, 0.012, '#8a6a3a', { pos: [0, -0.05, 0.05], rot: [0, Math.PI / 2, 0] }),
            part.sphere(0.05, '#6fbf73', { pos: [0.04, -0.09, 0.07] }),
            part.sphere(0.04, '#c94a3a', { pos: [-0.04, -0.09, 0.03] }),
          ]),
          sharedMaterials.cloth,
        ),
      );
      break;
    default:
      return null;
  }
  return g;
}

function buildJian(model: string, tint?: string): THREE.Object3D {
  const g = new THREE.Group();
  const bladeColor =
    model === 'sword_wood'
      ? '#b8865a'
      : model === 'sword_iron'
        ? '#9aa1a8'
        : model === 'sword_jade'
          ? '#9be8c0'
          : '#e1e6ea';
  const guard = model === 'sword_jade' ? '#d4a54a' : model === 'sword_wood' ? '#7a5332' : '#b08d3c';
  const len = 0.86;
  // Blade: flat diamond profile tapering to a point.
  const shape = new THREE.Shape();
  shape.moveTo(-0.022, 0);
  shape.lineTo(0.022, 0);
  shape.lineTo(0.019, -len + 0.08);
  shape.lineTo(0, -len);
  shape.lineTo(-0.019, -len + 0.08);
  shape.closePath();
  const blade = part.extrude(
    shape,
    0.008,
    tint && model !== 'sword_wood' ? blendHex(bladeColor, tint, 0.2) : bladeColor,
    { pos: [0, -0.1, 0] },
  );
  const hilt = merge([
    part.box(0.13, 0.025, 0.04, guard, { pos: [0, -0.09, 0] }),
    part.cyl(0.016, 0.018, 0.16, '#2a1d16', { pos: [0, 0, 0] }),
    part.sphere(0.024, guard, { pos: [0, 0.09, 0] }),
    part.cone(0.02, 0.09, '#b0302a', { pos: [0, 0.16, 0], rot: [Math.PI, 0, 0] }, 6),
  ]);
  const bladeMesh = new THREE.Mesh(blade, model === 'sword_jade' ? jadeBlade : sharedMaterials.metal);
  g.add(bladeMesh, new THREE.Mesh(hilt, sharedMaterials.metal));
  bladeMesh.name = 'blade';
  return g;
}

const jadeBlade = new THREE.MeshStandardMaterial({
  vertexColors: true,
  roughness: 0.25,
  metalness: 0.3,
  emissive: new THREE.Color('#2fbf7a'),
  emissiveIntensity: 0.6,
});

function buildSaber(): THREE.Object3D {
  const shape = new THREE.Shape();
  shape.moveTo(-0.025, 0);
  shape.lineTo(0.03, 0);
  shape.quadraticCurveTo(0.07, -0.45, 0.02, -0.8);
  shape.lineTo(-0.035, -0.62);
  shape.quadraticCurveTo(0.0, -0.35, -0.025, 0);
  const g = new THREE.Group();
  g.add(
    new THREE.Mesh(part.extrude(shape, 0.01, '#b9bec4', { pos: [0, -0.1, 0] }), sharedMaterials.metal),
    new THREE.Mesh(
      merge([
        part.box(0.11, 0.03, 0.05, '#5a4630', { pos: [0, -0.09, 0] }),
        part.cyl(0.018, 0.02, 0.17, '#3a2a1c', { pos: [0, 0, 0] }),
        part.sphere(0.024, '#5a4630', { pos: [0, 0.095, 0] }),
      ]),
      sharedMaterials.metal,
    ),
  );
  return g;
}

function blendHex(a: string, b: string, t: number): string {
  const c = new THREE.Color(a).lerp(new THREE.Color(b), t);
  return `#${c.getHexString()}`;
}

/** Maps an item definition id to a weapon model key. */
export function weaponModelFor(itemModel: string | undefined): string | null {
  return itemModel ?? null;
}
